import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  signal,
  WritableSignal,
  input,
  viewChild,
  output,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10N_LOCALE, L10nTranslatePipe, L10nTranslationModule } from 'angular-l10n';
import { DragDropDirective } from '../drag-drop/drag-drop.directive';
import { getFilesFromInputTarget } from '../model/utility';
import { SpreadsheetPreviewDialogComponent } from '../spreadsheet-preview-dialog/spreadsheet-preview-dialog.component';
import { LogHelper } from '../logging/log-helper';

export interface ValidationDetail {
  summary: string;
  details?: string[];
  severity: 'error' | 'warning';
}

export interface FileSelection {
  file: File;
  selected: boolean;
  validationMessage?: string;
  validationDetails?: ValidationDetail;
}

export interface FileValidationResult {
  valid: boolean;
  details?: ValidationDetail;
}

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    DragDropDirective,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatTooltipModule,
    L10nTranslatePipe,
    L10nTranslationModule,
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent {
  private readonly dialog = inject(MatDialog);
  public readonly locale = inject(L10N_LOCALE);

  readonly maxFiles = input<number>(6);
  readonly acceptedTypes = input<string>('.csv,.xlsx,.xls');
  readonly autoSelectAll = input<boolean>(true);
  readonly defaultText = input<string>('Sftp.Upload.Upload.DragAndDrop');
  readonly successText = input<string>('Sftp.Upload.Upload.Dropped');
  readonly errorText = input<string>('Sftp.Upload.Upload.FileError');
  readonly maxPreviewRows = input<number>(50);
  readonly validateFile = input<((file: File) => Promise<FileValidationResult>) | null>(null);

  readonly filesSelected = output<File[]>();
  readonly filesChanged = output<File[]>();

  readonly dragDropRef = viewChild<DragDropDirective>('dragDropRef');
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  allFiles: WritableSignal<FileSelection[]> = signal<FileSelection[]>([]);
  selectedFiles = computed<File[]>(() =>
    this.allFiles()
      .filter((f) => f.selected)
      .map((f) => f.file),
  );
  totalFileCount = computed(() => this.allFiles().length);
  selectedFileCount = computed(() => this.selectedFiles().length);

  async setFiles(files: File[]): Promise<void> {
    const fileSelections: FileSelection[] = [];

    for (const file of files) {
      let validationMessage: string | undefined;
      let validationDetails: ValidationDetail | undefined;

      const validator = this.validateFile();
      if (validator) {
        try {
          const result = await validator(file);
          if (!result.valid) {
            validationDetails = result.details;
          }
        } catch (error) {
          if (error) {
            LogHelper.debug('FileUploadComponent', 'File validation error:', error);
          }
          validationMessage = 'Failed to validate file';
        }
      }

      const hasValidation = !!(validationMessage || validationDetails);

      fileSelections.push({
        file,
        selected: this.autoSelectAll() && !hasValidation,
        validationMessage,
        validationDetails,
      });
    }

    this.allFiles.set(fileSelections);
    this.emitChanges();
  }

  async setFilesFromInput(target: EventTarget) {
    const files = getFilesFromInputTarget(target);
    try {
      await this.setFiles(files);
    } catch (error) {
      if (error) {
        LogHelper.debug('FileUploadComponent', 'Error setting files:', error);
        this.allFiles.set([]);
      }
    }
  }

  async setFilesFromDrop(files: File[]) {
    try {
      await this.setFiles(files);
    } catch (error) {
      if (error) {
        LogHelper.debug('FileUploadComponent', 'Error setting files from drop:', error);
        this.allFiles.set([]);
      }
    }
  }

  toggleFileSelection(index: number): void {
    this.allFiles.update((files) => files.map((f, i) => (i === index ? { ...f, selected: !f.selected } : f)));
    this.emitChanges();
  }

  removeFile(index: number): void {
    this.allFiles.update((files) => files.filter((_, i) => i !== index));
    this.emitChanges();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  clickFileInput(): void {
    this.fileInput()?.nativeElement.click();
  }

  clearFiles(): void {
    this.allFiles.set([]);
    if (this.fileInput()?.nativeElement) {
      this.fileInput()!.nativeElement.value = '';
    }
    this.emitChanges();
  }

  private emitChanges(): void {
    const selected = this.selectedFiles();
    this.filesChanged.emit(selected);
    if (selected.length > 0) {
      this.filesSelected.emit(selected);
    }
  }

  canPreviewFile(file: File): boolean {
    return this.acceptedTypes()
      .toLowerCase()
      .includes(file.name.toLowerCase().split('.').pop() || '');
  }

  openSpreadsheetPreview(file: File): void {
    this.dialog.open(SpreadsheetPreviewDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '80vh',
      data: {
        file,
        maxPreviewRows: this.maxPreviewRows(),
      },
    });
  }
}
